import { Link } from "react-router-dom";
import {
  ArrowRight, Sparkles, Search, Scale, HeartPulse, Shield, Receipt,
  BookOpen, Compass, Wand2, MessageSquareText, Play, Lock, FileSearch,
  CheckCircle2, Eye, Library, Quote, ChevronRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import Disclaimer from "@/components/Disclaimer";
import { useSEO } from "@/lib/useSEO";

const NAV = [
  { id: "how", label: "How It Works", href: "#how" },
  { id: "features", label: "Features", href: "#features" },
  { id: "trust", label: "Trust", href: "#trust" },
  { id: "about", label: "About", href: "#about" },
];

const FEATURES = [
  { icon: Search, title: "AI Powered Insurance Search", desc: "Find the right policy with natural questions instead of filters." },
  { icon: Scale, title: "Policy Comparison Engine", desc: "Side-by-side breakdowns of coverage, premiums, and exclusions." },
  { icon: HeartPulse, title: "Health Insurance Analysis", desc: "Decode brochures: waiting periods, sub-limits and network hospitals." },
  { icon: Shield, title: "Life Insurance Guidance", desc: "Term vs ULIP vs endowment — explained for your life stage." },
  { icon: Receipt, title: "Tax Benefit Discovery", desc: "Section 80C, 80D and 10(10D) made simple with real examples." },
  { icon: BookOpen, title: "Policy Simplification", desc: "Paste any clause, get plain-English explanation in seconds." },
  { icon: Compass, title: "Coverage Analysis", desc: "Spot gaps in your existing policies before they cost you." },
  { icon: Wand2, title: "Personalized Recommendations", desc: "Tailored to your age, family, health, income and goals." },
];

const STEPS = [
  { n: "01", title: "Tell InsureGenie about yourself", desc: "Share your age, family, health and goals — naturally, in chat." },
  { n: "02", title: "AI searches insurance knowledge", desc: "Pulls from real policy documents and public IRDAI sources." },
  { n: "03", title: "AI analyzes the policies", desc: "Cross-checks waiting periods, exclusions, sub-limits and premiums." },
  { n: "04", title: "Receive explanations & recommendations", desc: "Clear answers, comparisons and follow-up suggestions." },
];

const TRUST = [
  { icon: FileSearch, label: "Real Policy Based Insights" },
  { icon: Eye, label: "Transparent Explanations" },
  { icon: Library, label: "Source Backed Answers" },
  { icon: Lock, label: "Privacy Focused" },
  { icon: BookOpen, label: "Educational Guidance" },
];

const SAMPLE_PROMPTS = [
  "Which health insurance is best for a family of four?",
  "Compare Star Health and Niva Bupa.",
  "Explain this policy in simple English.",
  "Which policy covers maternity?",
  "Best insurance plan for a diabetic aged 40.",
  "How much tax can I save through health insurance?",
];

export default function Landing() {
  useSEO({
    title: "InsureGenie — AI Insurance Expert for India | Health, Term & 80D Tax",
    description: "India's AI insurance advisor. Compare health, term life and tax-saving (Section 80D) policies from Star Health, HDFC Life, LIC, Niva Bupa, ICICI Prudential and more — through natural conversation. Free to start.",
  });
  return (
    <div data-testid="landing-page" className="min-h-screen bg-background text-foreground flex flex-col">
      <Disclaimer variant="banner" />

      {/* Nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/75 border-b border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <a
                key={n.id}
                data-testid={`nav-${n.id}`}
                href={n.href}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/auth" className="hidden sm:inline-flex">
              <Button data-testid="nav-signin-btn" variant="ghost" className="rounded-full">Sign in</Button>
            </Link>
            <Link to="/auth?mode=register">
              <Button data-testid="nav-start-chatting-btn" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-md">
                Start Chatting <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern bg-grid opacity-50 [mask-image:radial-gradient(ellipse_at_center,black_0%,transparent_70%)]" />
        <div
          className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/34337252/pexels-photo-34337252.jpeg')",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-20 pb-24 sm:pt-28 sm:pb-32">
          <div className="flex flex-col items-center text-center">
            <Badge data-testid="hero-badge" className="rounded-full bg-accent/10 text-accent border border-accent/20 px-3 py-1 mb-6 font-medium">
              <span className="mr-1.5" role="img" aria-label="India">🇮🇳</span> Made for Indian insurance customers
            </Badge>
            <h1 data-testid="hero-headline" className="font-display text-4xl sm:text-6xl lg:text-7xl font-semibold tracking-tight text-balance max-w-4xl">
              Meet <span className="text-primary">Insure</span><span className="text-secondary">Genie</span>
            </h1>
            <p data-testid="hero-subheadline" className="mt-5 font-display text-xl sm:text-2xl text-muted-foreground font-medium">
              Your AI Insurance Expert for India.
            </p>
            <p data-testid="hero-description" className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed text-balance">
              Ask anything about health insurance, term life, policy benefits, Section 80D tax savings, waiting periods and coverage — grounded in IRDAI regulations and real Indian insurer documents. Powered by Claude Sonnet 4.5.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center gap-3">
              <Link to="/auth?mode=register">
                <Button data-testid="hero-start-chatting-btn" size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-7 h-12 shadow-lg group">
                  Start Chatting
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-0.5 transition-transform" />
                </Button>
              </Link>
              <a href="#demo">
                <Button data-testid="hero-watch-demo-btn" size="lg" variant="outline" className="rounded-full h-12 px-7 border-border hover:bg-muted">
                  <Play className="w-4 h-4 mr-2" /> Watch Demo
                </Button>
              </a>
            </div>

            {/* Chat preview card */}
            <div id="demo" className="mt-16 w-full max-w-4xl">
              <div className="relative glass-panel rounded-3xl p-2 sm:p-3 shadow-2xl">
                <div className="rounded-2xl bg-card border border-border/60 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-muted/40">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                    </div>
                    <span className="text-[11px] text-muted-foreground tracking-widest uppercase">insuregenie.ai/chat</span>
                    <span className="w-12" />
                  </div>
                  <div className="p-5 sm:p-7 text-left space-y-5">
                    <div className="flex gap-3 justify-end">
                      <div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[80%] text-sm shadow-sm">
                        Compare Star Health and Niva Bupa for a family of four.
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-lg bg-accent/15 text-accent flex items-center justify-center flex-shrink-0">
                        <Sparkles className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-foreground leading-relaxed">
                          Here&apos;s a side-by-side for a family floater of 4:
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-px bg-border rounded-xl overflow-hidden text-[12.5px]">
                          <div className="bg-muted p-2.5 font-semibold">Parameter</div>
                          <div className="bg-muted p-2.5 font-semibold">Star Comprehensive</div>
                          <div className="bg-muted p-2.5 font-semibold">Niva Bupa Aspire</div>
                          <div className="bg-card p-2.5">Sum Insured</div>
                          <div className="bg-card p-2.5">₹5L–₹1Cr</div>
                          <div className="bg-card p-2.5">₹3L–₹3Cr</div>
                          <div className="bg-card p-2.5">Room Rent</div>
                          <div className="bg-card p-2.5">No cap</div>
                          <div className="bg-card p-2.5">No cap (Aspire+)</div>
                          <div className="bg-card p-2.5">Waiting (PED)</div>
                          <div className="bg-card p-2.5">2 years</div>
                          <div className="bg-card p-2.5">2 years (3 yrs base)</div>
                        </div>
                        <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-accent">
                          <CheckCircle2 className="w-3.5 h-3.5" /> Source: official policy brochures · IRDAI guidelines
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <div className="text-[12px] text-muted-foreground inline-flex items-center gap-2">
                        <span className="typing-dot bg-muted-foreground/60" />
                        <span className="typing-dot bg-muted-foreground/60" />
                        <span className="typing-dot bg-muted-foreground/60" />
                        InsureGenie is typing…
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Suggested prompts row */}
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {SAMPLE_PROMPTS.map((p, i) => (
                  <Link
                    key={i}
                    to={`/auth?mode=register&prompt=${encodeURIComponent(p)}`}
                    data-testid={`hero-prompt-${i}`}
                    className="text-[12.5px] text-muted-foreground bg-card border border-border rounded-full px-3 py-1.5 hover:border-accent hover:text-foreground hover:bg-muted/50 transition-all"
                  >
                    {p}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="border-t border-border/60 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">How it works</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              From question to clarity in four steps.
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                data-testid={`step-${i + 1}`}
                className="relative bg-card border border-border rounded-2xl p-6 hover:border-accent/40 transition-all duration-300"
              >
                <div className="font-display text-3xl font-semibold text-secondary/40">{s.n}</div>
                <h3 className="mt-4 font-display text-lg font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <ChevronRight className="hidden lg:block absolute top-1/2 -right-4 -translate-y-1/2 w-5 h-5 text-border" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">Capabilities</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Insurance intelligence, beautifully simple.
            </h2>
            <p className="mt-4 text-muted-foreground text-base sm:text-lg">
              Eight focused capabilities — one conversational interface.
            </p>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className="group bg-card border border-border rounded-2xl p-6 hover:-translate-y-1 hover:border-accent/40 hover:shadow-lg transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center group-hover:bg-accent/15 group-hover:text-accent transition-colors">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-display text-base font-semibold tracking-tight">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Capabilities (reasoning) */}
      <section className="border-t border-border/60 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          background: "radial-gradient(900px 400px at 50% 0%, rgba(20,184,166,0.35), transparent 60%)",
        }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">AI reasoning</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                Watch InsureGenie think — step by step.
              </h2>
              <p className="mt-5 text-white/75 leading-relaxed text-balance">
                InsureGenie surfaces its reasoning chain: which documents it consulted, the
                exclusions it weighed, and the tradeoffs it considered. No more black-box answers.
              </p>
              <div className="mt-6 space-y-2.5">
                {[
                  "Understands policy documents and brochures",
                  "Explains waiting periods and exclusions",
                  "Compares plans across insurers",
                  "Analyzes tax benefits & section eligibility",
                  "Provides personalized recommendations",
                ].map((c, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-white/85">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" /> {c}
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="glass-panel rounded-2xl p-5 bg-white/5 border-white/10 text-white">
                <div className="flex items-center gap-2 text-xs text-white/70 mb-4">
                  <Sparkles className="w-3.5 h-3.5 text-accent" /> Reasoning trace
                </div>
                <ol className="relative space-y-4 ml-1">
                  {[
                    "Identify user's profile: family of 4, age 35, mild hypertension.",
                    "Filter products: family floater plans with PED coverage ≤ 2 yrs.",
                    "Score plans by sub-limits, room rent caps, claim ratio.",
                    "Cross-check exclusions on lifestyle conditions.",
                    "Surface top 3 with transparent tradeoff explanation.",
                  ].map((t, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-accent/20 text-accent text-[11px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                      <span className="text-white/85 leading-relaxed">{t}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 inline-flex items-center gap-2 text-[12px] text-accent">
                  <Quote className="w-3.5 h-3.5" /> Final answer with confidence: High (87%)
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust */}
      <section id="trust" className="border-t border-border/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">Why teams trust us</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight max-w-3xl mx-auto">
            Built for honesty. Designed for clarity.
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-4">
            {TRUST.map((t, i) => (
              <div
                key={t.label}
                data-testid={`trust-${i}`}
                className="bg-card border border-border rounded-2xl p-5 flex flex-col items-center gap-3 hover:border-accent/40 transition-colors"
              >
                <div className="w-10 h-10 rounded-xl bg-accent/10 text-accent flex items-center justify-center">
                  <t.icon className="w-5 h-5" />
                </div>
                <span className="text-sm font-medium text-foreground leading-tight">{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="about" className="border-t border-border/60 bg-muted/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 text-center">
          <MessageSquareText className="w-8 h-8 text-accent mx-auto mb-4" />
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight max-w-2xl mx-auto">
            Start your first conversation with InsureGenie.
          </h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Free to try. No spam. No sales calls. Just clear, educational answers about insurance.
          </p>
          <Link to="/auth?mode=register" className="inline-block mt-8">
            <Button data-testid="cta-start-chatting-btn" size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 shadow-lg">
              Start Chatting <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/60 bg-background mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex flex-col md:flex-row gap-8 justify-between">
            <div className="max-w-sm">
              <Logo />
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                India's AI Insurance Expert — built to make Indian health, term and general insurance feel less like fine print and more like a conversation.
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-sm">
              {[
                { h: "Product", l: [["Features", "/#features"], ["How It Works", "/#how"], ["Trust", "/#trust"]] },
                { h: "Company", l: [["About", "/about"], ["Contact", "/contact"]] },
                { h: "Legal", l: [["Privacy Policy", "/privacy"], ["Terms", "/terms"], ["Disclaimer", "/disclaimer"]] },
                { h: "Resources", l: [["Chat", "/chat"], ["Sign in", "/auth"]] },
              ].map((col) => (
                <div key={col.h}>
                  <div className="font-display font-semibold text-foreground mb-3">{col.h}</div>
                  <ul className="space-y-2 text-muted-foreground">
                    {col.l.map(([label, to]) => (
                      <li key={label}><Link to={to} className="hover:text-foreground transition-colors">{label}</Link></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <Disclaimer />
          <div className="mt-6 text-[12px] text-muted-foreground flex flex-col sm:flex-row gap-2 justify-between">
            <span>© {new Date().getFullYear()} InsureGenie. All rights reserved.</span>
            <span className="inline-flex items-center gap-1.5">
              <span role="img" aria-label="India">🇮🇳</span>
              Made in India · Built for Indian insurance customers
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
